import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTargets, getDependencies } from '../../../services/scm/Dependencies';
import { getPackageInfo } from '../../../services/scm/Package';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSyncAlt, faXmark, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import './SelectTargets.css';
import { useLocation, useNavigate } from 'react-router-dom';
import Grid from "../../../components/grid/Grid";
import { getTargetsforPackage } from '../../../services/scm/Deployments';

export default function SelectTargets() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const navState = (location?.state) || {};

  const pack = navState.packageName ?? '';
  const ver = navState.versionName ?? '';
  const jobType = navState.jobType ?? '';
  const isSnapshotJob = jobType === 'SNAPSHOT';

  const params = useMemo(
    () => ({ packageName: pack, versionName: ver }),
    [pack, ver]
  );

  // Dynamic page title
  const pageTitle = isSnapshotJob
    ? t('Targets for SNAPSHOT Deployment')
    : t('Targets for CONTINUOUS Deployment');
  const [selectedRows, setSelectedRows] = useState([]);
  const [data, setData] = useState([]);
  const [loadingTargets, setLoadingTargets] = useState(false);

  const headers = [
    { key: "thingName", label: "Thing Name", width: "50%" },
    { key: "thingTypeName", label: "Thing Type", width: "50%" },
  ];

  const loadTargets = async () => {
    try {
      setLoadingTargets(true);
      const raw = await getTargetsforPackage(params);
      const items = raw?.things || [];
      setData(
        items.map((item, i) => ({
          id: i + 1,
          thingName: item.thingName || "-",
          thingTypeName: item.thingTypeName || "-",
          thingArn: item.thingArn || "-"
        }))
      );
    } finally {
      setLoadingTargets(false);
    }
  };

  const loadAllTargets = async () => {
    try {
      setLoadingTargets(true);
      const raw = await getTargets();
      const items = raw?.things || raw?.items || [];
      setData(
        items.map((item, i) => ({
          id: i + 1,
          thingName: item.thingName || item.name || "-",
          thingTypeName: item.thingTypeName || "-",
          thingArn: item.thingArn || item.arn || "-"
        }))
      );
    } finally {
      setLoadingTargets(false);
    }
  };

  const [gridKey, setGridKey] = useState(0);
  const clearSnapshotSelection = () => {
    setSelectedRows([]);
    setGridKey(prev => prev + 1);
  };
  const [allGroups, setAllGroups] = useState([]);
  const [addedGroups, setAddedGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [depLoading, setDepLoading] = useState(false);

  // ARNs from package info
  const [depArns, setDepArns] = useState([]);

  const formatFilters = (filters = {}) => {
    const keys = Object.keys(filters);
    if (!keys.length) return t("None");
    return keys
      .map(k => `${k}: ${Array.isArray(filters[k]) ? filters[k].join(', ') : filters[k]}`)
      .join(" & ");
  };

  // Load package info → get dependencies (ARNs)
  const loadPackageInfo = async () => {
    try {
      if (!pack || !ver) return;

      const res = await getPackageInfo(params);
      const pkg = Array.isArray(res)
        ? res.find(
          item =>
            item.packageName === pack &&
            (item.version === ver || item.versionName === ver)
        )
        : res;

      const deps = (pkg?.dependencies || []).filter(Boolean);
      setDepArns(deps);
      return deps;
    } catch (e) {
      console.error('loadPackageInfo error:', e);
      setDepArns([]);
      return [];
    }
  };

  // Load all dependency groups
  const loadDependencies = async () => {
    try {
      setDepLoading(true);
      const raw = await getDependencies();
      const arr = Array.isArray(raw) ? raw : (raw?.data ?? []);

      setAllGroups(
        arr
          .map(x => ({
            thingGroupName: x.thingGroupName ?? x.name,
            thingGroupArn: x.thingGroupArn ?? x.arn ?? x.id,
            filters: x.filters ?? {}
          }))
          .filter(x => x.thingGroupName && x.thingGroupArn)
      );
    } finally {
      setDepLoading(false);
    }
  };

  const dropdownGroups = useMemo(
    () =>
      allGroups.filter(g =>
        depArns.length > 0 ? depArns.includes(g.thingGroupArn) : false
      ),
    [allGroups, depArns]
  );



  const addGroup = (name) => {
    if (!name) return;
    if (addedGroups.some(g => g.thingGroupName === name)) return;
    const g = dropdownGroups.find(x => x.thingGroupName === name);
    if (g) setAddedGroups(prev => [...prev, g]);
  };

  const removeGroup = (name) => {
    setAddedGroups(prev => prev.filter(g => g.thingGroupName !== name));
  };
  useEffect(() => {
    (async () => {
      const deps = await loadPackageInfo();
      await loadDependencies();

      if (isSnapshotJob) {
        if (deps && deps.length > 0) {
          await loadTargets();
        } else {
          await loadAllTargets();
        }
        return;
      }

      if (!deps || deps.length === 0) {
        await loadAllTargets();
      } else {
        setData([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.packageName, params.versionName, isSnapshotJob]);


  const handleRefreshSnapshot = () => {
    clearSnapshotSelection();

    if (isSnapshotJob) {
      if (depArns && depArns.length > 0) {
        loadTargets();
      } else {
        loadAllTargets();
      }
      return;
    }

    if (!depArns || depArns.length === 0) {
      loadAllTargets();
    }
  };


  const handleSave = () => {
    if (isSnapshotJob) {
      const targets = selectedRows.map(r => r.thingName);
      const targetsArn = selectedRows.map(r => r.thingArn);

      return navigate("/software-management/deployment-summary", {
        state: {
          ...navState,
          targets,
          targetsArn
        }
      });
    }

    return navigate("/software-management/deployment-summary", {
      state: {
        ...navState,
        dependencyGroupNames: addedGroups.map(g => g.thingGroupName),
        dependencyGroupsArn: addedGroups.map(g => g.thingGroupArn)
      }
    });
  };

  const isSaveDisabled =
    isSnapshotJob
      ? selectedRows.length === 0
      : addedGroups.length === 0; // at least one dependency group

  return (
    <div className="container my-3 track-deployment-package h-100">
      <div className="col-12 col-xxl-10 responsive-col d-flex flex-column h-100">

        {/* HEADER */}
        <div className="row mb-3">
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-transperant"
              onClick={() =>
                navigate("/software-management/create-deployment", {
                  state: {
                    ...navState
                  }
                })
              }
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>

            <h5 className="card-title mb-0">{pageTitle}</h5>
          </div>
        </div>

        {/* TOP BAR / CAPTION */}
        <div className="d-flex align-items-center mb-0">
          <div className="me-auto">
            {isSnapshotJob
              ? t(`Selected Rows (${selectedRows.length})`)
              : ''}
          </div>

          <div className="action-buttons">
            {isSnapshotJob && (
              <>
                <button
                  className="btn btn-outline-secondary me-2"
                  onClick={clearSnapshotSelection}
                >
                  {t("Clear")}
                </button>

                <button
                  className="btn btn-primary"
                  onClick={handleRefreshSnapshot}
                >
                  <FontAwesomeIcon icon={faSyncAlt} className="me-1" />
                  {t("Refresh")}
                </button>
              </>
            )}
          </div>

        </div>

        {/* MAIN CONTENT */}
        <div className="grid-wrapper">
          {isSnapshotJob ? (
            <Grid
              key={gridKey}
              headers={headers}
              data={data}
              rowsPerPage={50}
              canMultiSelect={true}
              onSelectionChange={setSelectedRows}
              selectedRows={selectedRows}
              loading={loadingTargets}
              sortable={true}
            />
          ) : (
            // CONTINUOUS: dependency-group selection
            <>
              {/* INFO BANNER */}
              <div className="card mt-2 bg-light no-border">
                <div className="card-body small text-muted d-flex align-items-start">
                  <FontAwesomeIcon icon={faInfoCircle} className="me-2 mt-1 text-primary" />
                  <span>
                    {t('Deployment will apply only to devices that belong to ANY of the selecteddependency groups configured for this package. Only those groups are shown below. At least one dependency group must be selected.')}
                  </span>
                </div>
              </div>

              {/* GROUP DROPDOWN & LIST */}
              <div className="card mt-3 no-border">
                <div className="card-body">
                  {/* DROPDOWN */}
                  <div className="col-12 col-md-6">
                    <select
                      className="form-select"
                      value={selectedGroup}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedGroup(value);
                        addGroup(value);
                        setSelectedGroup('');
                      }}
                    >
                      <option value="">{t('Select Dependency Groups')}</option>
                      {dropdownGroups
                        .filter(
                          g =>
                            !addedGroups.some(a => a.thingGroupName === g.thingGroupName)
                        )
                        .map(g => (
                          <option key={g.thingGroupArn} value={g.thingGroupName}>
                            {g.thingGroupName}
                          </option>
                        ))}
                    </select>
                    {dropdownGroups.length === 0 && !depLoading && (
                      <div className="small text-muted mt-1">
                        {t('No dependency groups are configured for this package.')}
                      </div>
                    )}
                  </div>

                  {/* SELECTED LIST */}
                  <div className="mt-4">
                    <h6>{t('Selected Groups')}</h6>

                    {addedGroups.length === 0 ? (
                      <div className="text-muted">
                        {depLoading ? t("Loading...") : t("No Groups selected")}
                      </div>
                    ) : (
                      <div className="table-responsive ad-table-wrap">
                        <table className="table table-sm align-middle">
                          <thead>
                            <tr>
                              <th>{t('Dependency Group')}</th>
                              <th>{t('Filters')}</th>
                              <th className="text-end"></th>
                            </tr>
                          </thead>

                          <tbody>
                            {addedGroups.map(g => (
                              <tr key={g.thingGroupArn}>
                                <td>{g.thingGroupName}</td>
                                <td className="text-muted">{formatFilters(g.filters)}</td>
                                <td className="text-end">
                                  <button
                                    className="btn btn-sm btn-light border-0 text-danger p-1"
                                    onClick={() => removeGroup(g.thingGroupName)}
                                  >
                                    <FontAwesomeIcon icon={faXmark} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="action-footer mt-3">
          <button
              className="btn btn-outline-secondary"
              onClick={() =>
                navigate("/software-management/create-deployment", {
                  state: {
                    ...navState
                  }
                })
              }
            >
              {t("Back")}
            </button>
          <button
            className="btn btn-primary"
            disabled={isSaveDisabled}
            onClick={handleSave}
          >
            {t("Next")}
          </button>
        </div>
      </div>
    </div>
  );
}